import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Switch, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Alert, Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { api } from '../../api/client.js';
import { UserRole } from '../../../shared/types.js';
import { colors } from '../../styles/theme.js';

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  active: boolean;
  created_at: string;
}

const roleColors: Record<string, string> = {
  [UserRole.ADMIN]: colors.scoreRed,
  [UserRole.DIGA]: colors.scoreYellow,
  [UserRole.USER]: colors.scoreGreen,
};

export default function UsersPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [editUser, setEditUser] = useState<UserRow | null>(null);
  const [form, setForm] = useState({ email: '', name: '', role: UserRole.USER as UserRole, password: '' });
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const loadUsers = async () => {
    const res = await api.get<{ users: UserRow[] }>('/admin/users');
    if (res.success) setUsers(res.data.users);
    else setError(res.error.message);
  };

  useEffect(() => { loadUsers(); }, []);

  const openCreate = () => {
    setDialogMode('create');
    setForm({ email: '', name: '', role: UserRole.USER, password: '' });
  };

  const openEdit = (user: UserRow) => {
    setDialogMode('edit');
    setEditUser(user);
    setForm({ email: user.email, name: user.name, role: user.role, password: '' });
  };

  const handleSave = async () => {
    setError(null);
    if (dialogMode === 'create') {
      const res = await api.post('/admin/users', form);
      if (!res.success) { setError((res as { success: false; error: { message: string } }).error.message); return; }
    } else if (dialogMode === 'edit' && editUser) {
      const body: Record<string, unknown> = { name: form.name, role: form.role };
      const hadPassword = !!form.password;
      if (form.password) body.password = form.password;
      const res = await api.patch(`/admin/users/${editUser.id}`, body);
      if (!res.success) { setError((res as { success: false; error: { message: string } }).error.message); return; }
      if (hadPassword) setSnackbar('Passwort wurde aktualisiert');
      else setSnackbar('Änderungen wurden gespeichert');
    }
    setDialogMode(null);
    loadUsers();
  };

  const handleToggleActive = async (user: UserRow) => {
    setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, active: !u.active } : u));
    await api.patch(`/admin/users/${user.id}`, { active: !user.active });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">User-Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Neuer User</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}` }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: colors.lightGrey }}>
              <TableCell>E-Mail</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Rolle</TableCell>
              <TableCell>Erstellt</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>
                  <Chip label={user.role} size="small" sx={{ bgcolor: roleColors[user.role], color: '#fff', fontWeight: 600 }} />
                </TableCell>
                <TableCell>{new Date(user.created_at).toLocaleDateString('de-DE')}</TableCell>
                <TableCell>
                  <Switch checked={user.active} onChange={() => handleToggleActive(user)} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => openEdit(user)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!dialogMode} onClose={() => setDialogMode(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogMode === 'create' ? 'Neuen User anlegen' : 'User bearbeiten'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="E-Mail" value={form.email} disabled={dialogMode === 'edit'}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <TextField label="Name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <FormControl>
            <InputLabel>Rolle</InputLabel>
            <Select value={form.role} label="Rolle" onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}>
              <MenuItem value={UserRole.USER}>user</MenuItem>
              <MenuItem value={UserRole.DIGA}>diga</MenuItem>
              <MenuItem value={UserRole.ADMIN}>admin</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Passwort" type="password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            helperText={dialogMode === 'edit' ? 'Leer lassen um Passwort nicht zu ändern' : 'Optional — User kann Magic Link verwenden'} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMode(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSave}>
            {dialogMode === 'create' ? 'Anlegen' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(null)} severity="success" sx={{ width: '100%' }}>
          {snackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
}
